<template>
    <div class="price-calculations-search">
        <div class="search-query">
            <v-text-field
                v-model="form.search"
                :label="$t('general.search')"
                append-icon="far fa-search"
                clear-icon="fal fa-times"
                clearable
                single-line
                hide-details
                @input="searchDebounce"
            />

            <v-btn
                v-if="!queryOnly"
                class="ml-3 mt-3"
                color="primary"
                icon
                @click="showForm = !showForm"
            >
                <v-icon>fal fa-filter</v-icon>
            </v-btn>
        </div>

        <v-slide-y-transition>
            <div
                v-if="showForm"
                class="search-form"
            >
                <v-row>
                    <v-col>
                        <v-autocomplete
                            v-model="form.client"
                            :items="clients"
                            item-text="name"
                            item-value="id"
                            :filter="filterClient"
                            :hint="$t('price_calculations.supplier_hint')"
                            :persistent-hint="!form.client"
                            :label="$tc('general.supplier', 1)"
                            menu-props="offsetY"
                            clear-icon="fal fa-times"
                            clearable
                            auto-select-first
                            :error-messages="form.$errors.client"
                            @blur="form.validate('client')"
                            @input="delete form.$errors.client"
                        />
                    </v-col>
                </v-row>

                <v-row>
                    <v-col cols="8">
                        <v-menu
                            v-model="picker1"
                            :close-on-content-click="false"
                            :nudge-right="32"
                            transition="scale-transition"
                            min-width="290"
                            offset-y
                        >
                            <template v-slot:activator="{ on }">
                                <v-text-field
                                    v-model="form.startDate"
                                    :error-messages="form.$errors.startDate"
                                    :label="$t('general.start_date')"
                                    prepend-icon="fal fa-calendar"
                                    clear-icon="fal fa-times"
                                    clearable
                                    readonly
                                    v-on="on"
                                    @blur="form.validate('startDate')"
                                    @input="delete form.$errors.startDate"
                                />
                            </template>
                            <v-date-picker
                                v-model="form.startDate"
                                @input="picker1 = false; form.validate('startDate')"
                            />
                        </v-menu>
                    </v-col>

                    <v-col cols="4">
                        <v-text-field
                            v-model="form.startTime"
                            v-mask="'##:##:##'"
                            placeholder="hh:mm:ss"
                            :label="$t('general.time')"
                            :error-messages="form.$errors.startTime"
                            @blur="form.validate('startTime'); form.validate('endTime');"
                            @input="delete form.$errors.startTime"
                        />
                    </v-col>
                </v-row>

                <v-row>
                    <v-col class="py-0" cols="8">
                        <v-menu
                            v-model="picker2"
                            :close-on-content-click="false"
                            :nudge-right="32"
                            transition="scale-transition"
                            min-width="290"
                            offset-y
                        >
                            <template v-slot:activator="{ on }">
                                <v-text-field
                                    v-model="form.endDate"
                                    :error-messages="form.$errors.endDate"
                                    :label="$t('general.end_date')"
                                    prepend-icon="fal fa-calendar"
                                    clear-icon="fal fa-times"
                                    clearable
                                    readonly
                                    v-on="on"
                                    @blur="form.validate('endDate')"
                                />
                            </template>
                            <v-date-picker
                                v-model="form.endDate"
                                :min="form.startDate"
                                @input="picker2 = false; form.validate('endDate')"
                            />
                        </v-menu>
                    </v-col>

                    <v-col class="py-0" cols="4">
                        <v-text-field
                            v-model="form.endTime"
                            v-mask="'##:##:##'"
                            placeholder="hh:mm:ss"
                            :label="$t('general.time')"
                            :error-messages="form.$errors.endTime"
                            @blur="form.validate('endTime')"
                            @input="delete form.$errors.endTime"
                        />
                    </v-col>
                </v-row>

                <div class="d-flex justify-center mt-4">
                    <v-btn
                        color="primary"
                        outlined
                        @click="search"
                    >
                        {{ $t('general.search') }}
                    </v-btn>
                </div>
            </div>
        </v-slide-y-transition>
    </div>
</template>

<script>
import _ from 'lodash';
import { required, helpers } from 'vuelidate/lib/validators';

export default {
    props: {
        filter: {
            type: Object,
            default: () => null
        },
        queryOnly: {
            type: Boolean,
            default: false
        },
        clients: {
            type: Array,
            default: () => []
        }
    },

    data() {
        return {
            showForm: false,
            picker1: false,
            picker2: false,
            form: this.createForm(),
        };
    },

    validations() {
        return {
            form: {
                startDate: { required: required },
                startTime: {
                    required: required,
                    time: helpers.withParams(
                        { type: 'time' },
                        function(value) {
                            if(!value) return true

                            return moment(value, 'HH:mm:ss', true).isValid()
                        }
                    )
                },
                endDate: { required: required },
                endTime: {
                    required: required,
                    timeAfter: helpers.withParams(
                        { type: 'timeAfter' },
                        function(value) {
                            if(!value) return true

                            if(!moment(value, 'HH:mm:ss', true).isValid()) {
                                return false
                            }

                            if(!this.form.startDate || !this.form.startTime || !this.form.endDate) {
                                return false
                            }

                            return moment(`${this.form.endDate} ${value}`)
                                .isAfter(moment(`${this.form.startDate} ${this.form.startTime}`))
                        }
                    )
                },
            }
        }
    },

    watch: {
        filter(val) {
            this.form = this.createForm();
        }
    },

    methods: {
        createForm() {
            return new Form(this, 'form', {
                search: this.filter?.search || null,
                startDate: this.filter?.startDate ? moment(this.filter.startDate).format('YYYY-MM-DD') : null,
                startTime: this.filter?.startDate ? moment(this.filter.startDate).format('HH:mm:ss') : null,
                endDate: this.filter?.endDate ? moment(this.filter.endDate).format('YYYY-MM-DD') : null,
                endTime: this.filter?.endDate ? moment(this.filter.endDate).format('HH:mm:ss') : null,
                client: this.filter?.client || null,
            });
        },

        searchDebounce: _.debounce(function() {
            this.search();
        }, 500),

        search() {
            if(!this.form.validate()) return;
            this.showForm = false;

            const formData = this.form.toJson();

            this.$emit('update:filter', {
                search: formData.search,
                startDate: formData.startDate ? `${formData.startDate} ${formData.startTime || ''}` : null,
                endDate: formData.endDate ? `${formData.endDate} ${formData.endTime || ''}` : null,
                client: formData.client,
            });
        },

        filterClient(item, queryText, itemText) {
            return item.matches(queryText);
        }
    }
};
</script>

<style lang="scss" scoped>
.price-calculations-search {
    .search-query {
        display: flex;
        padding: 12px 0 20px 0;
        align-items: center;
    }

    .search-form {
        padding: 16px;
        border-radius: 3px;
        background: #fafafa;
        border: 1px solid #cccccc;
    }
}
</style>
